import type { EventCatalogEntry } from "@repo/vihangam-yog-shared";
import type { ReactNode } from "react";

import { OCTOBER } from "../../content/october";
import { Agenda } from "../../ui/Agenda";
import { Callout } from "../../ui/Callout";
import { Disclosure } from "../../ui/Disclosure";
import { EventWhere } from "../../ui/EventWhere";

import { KundCounter } from "./KundCounter";
import {
  hawanQuote,
  hawanTierCard,
  priceLines,
  type QuoteMember,
} from "./kunds";

interface DaySelection {
  stage: boolean;
  hawan: boolean;
  tierId: string;
  food: "vegetarian" | "jain" | "";
}

interface DayCardsProps {
  stage: EventCatalogEntry;
  hawan: EventCatalogEntry;
  /** The party as listed so far, for the live price under the option. */
  party: readonly QuoteMember[];
  value: DaySelection;
  onChange: (next: DaySelection) => void;
  soldOut: ReadonlySet<string>;
  disabled?: boolean;
  /** Submit problems pointing at the days or at the Sunday option. */
  dayNote?: string | null;
  tierNote?: string | null;
}

interface DayCardProps {
  id: string;
  checked: boolean;
  onToggle: () => void;
  event: EventCatalogEntry;
  /** The one line under the venue that decides the choice: the price, or
   *  that entry is free. */
  price: ReactNode;
  /** A photograph for the day, kept in view under the price. */
  image?: { src: string; alt: string };
  /** Everything else about the day, folded away until asked for: the
   *  practical lines, the running order. */
  details: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
}

function DayCard({
  id,
  checked,
  onToggle,
  event,
  price,
  image,
  details,
  children,
  disabled,
}: DayCardProps) {
  return (
    <div
      className={`rounded-2xl border bg-card p-5 transition duration-200 md:p-6 ${checked ? "border-magenta shadow-soft ring-1 ring-magenta" : "border-hairline hover:border-hairline-strong"}`}
    >
      <label
        htmlFor={id}
        className="flex cursor-pointer items-start gap-4 sm:items-center"
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          disabled={disabled}
          className="mt-1 h-5 w-5 shrink-0 accent-magenta sm:mt-0"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold tracking-wider text-teal-deep uppercase">
            {event.dateLabel}
          </span>
          <span className="font-display mt-1.5 block text-xl text-ink">
            {event.title}
          </span>
          {/* From the catalog; a day with no time yet shows no line. */}
          {event.timeLabel ? (
            <span className="mt-1 block text-sm text-ink">
              {event.timeLabel}
            </span>
          ) : null}
        </span>
      </label>
      {/* Outside the label: a Map link that toggled the day would be a trap. */}
      <div className="pl-9">
        <EventWhere event={event} className="mt-2" />
        <div className="mt-2 text-sm font-semibold text-ink">{price}</div>
        {image ? (
          <img
            src={image.src}
            alt={image.alt}
            loading="lazy"
            className="mt-3 aspect-[16/9] w-full rounded-xl object-cover shadow-soft"
          />
        ) : null}
        <Disclosure summary="More about this day" className="mt-2">
          {details}
        </Disclosure>
      </div>
      {checked && children ? (
        <div className="mt-6 border-t border-hairline pt-6">{children}</div>
      ) : null}
    </div>
  );
}

export function DayCards({
  stage,
  hawan,
  party,
  value,
  onChange,
  soldOut,
  disabled,
  dayNote = null,
  tierNote = null,
}: DayCardsProps) {
  const tiers = hawan.price?.tiers ?? [];
  const cur = hawan.price?.currency ?? "sgd";
  const hawanPrices = priceLines(hawan);
  const chosen = tiers.find((t) => t.id === value.tierId);
  const quote = chosen ? hawanQuote(party, chosen, cur) : null;
  return (
    <fieldset id="reg-days">
      <legend className="sr-only">Which days</legend>
      {dayNote ? (
        <p className="mt-1.5 text-sm text-crimson">{dayNote}</p>
      ) : null}
      <div className="mt-4 grid gap-4">
        <DayCard
          id="day-stage"
          checked={value.stage}
          onToggle={() => onChange({ ...value, stage: !value.stage })}
          event={stage}
          price="Free entry (with QR code)"
          details={
            <>
              <Callout>
                {OCTOBER.stage.notices.map((line) => (
                  <p key={line}>{line}</p>
                ))}
                {stage.arriveByLabel ? (
                  <p>Arrive by {stage.arriveByLabel}</p>
                ) : null}
              </Callout>
              <Agenda rows={stage.agenda} className="mt-3" />
            </>
          }
          disabled={disabled}
        />
        <DayCard
          id="day-hawan"
          checked={value.hawan}
          onToggle={() =>
            onChange({
              ...value,
              hawan: !value.hawan,
              tierId:
                !value.hawan && !value.tierId
                  ? (tiers.find((t) => !soldOut.has(t.id))?.id ?? "")
                  : value.tierId,
            })
          }
          event={hawan}
          price={
            hawanPrices.length > 0
              ? hawanPrices.map((line) => <p key={line}>{line}</p>)
              : "Price to be announced"
          }
          image={
            OCTOBER.hawan.poster
              ? {
                  src: OCTOBER.hawan.poster,
                  alt: OCTOBER.hawan.posterAlt ?? hawan.title,
                }
              : undefined
          }
          details={
            <>
              <Callout>
                <p>
                  Children under 16 come free, but list them so we can count
                  food.
                </p>
                <p>
                  Your Kund number will be shared closer to the day by email and
                  WhatsApp.
                </p>
                {OCTOBER.hawan.notices.map((line) => (
                  <p key={line}>{line}</p>
                ))}
                {hawan.arriveByLabel ? (
                  <p>Arrive by {hawan.arriveByLabel}</p>
                ) : null}
              </Callout>
              <KundCounter event={hawan} />
              <Agenda rows={hawan.agenda} className="mt-3" />
              {OCTOBER.hawan.objectives.length > 0 ? (
                <div className="mt-4">
                  <p className="eyebrow">Objectives of Vedic Hawan</p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ink">
                    {OCTOBER.hawan.objectives.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </>
          }
          disabled={disabled}
        >
          <p className="text-sm font-semibold text-ink">
            How would you like to take part?
          </p>
          <div
            id="hawan-tier"
            className="mt-2 grid gap-2"
            role="radiogroup"
            aria-label="Hawan Yajna option"
          >
            {tiers.map((t) => {
              const out = soldOut.has(t.id) || t.soldOut === true;
              const card = hawanTierCard(t, cur);
              return (
                <label
                  key={t.id}
                  className={`flex min-h-11 items-center gap-3 rounded-xl border p-3.5 transition-colors ${
                    out
                      ? "cursor-not-allowed border-hairline bg-ivory-deep"
                      : value.tierId === t.id
                        ? "cursor-pointer border-magenta bg-magenta/5"
                        : "cursor-pointer border-hairline hover:border-hairline-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="hawan-tier"
                    value={t.id}
                    checked={value.tierId === t.id}
                    disabled={disabled || out}
                    onChange={() => onChange({ ...value, tierId: t.id })}
                    className="h-5 w-5 shrink-0 accent-magenta"
                  />
                  <span
                    className={`flex-1 text-sm ${out ? "text-ink-soft" : "text-ink"}`}
                  >
                    <span className="font-semibold">{card.title}</span>
                    <span className="text-ink-soft">
                      {" "}
                      · {out ? "Sold out" : card.detail}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {tierNote ? (
            <p className="mt-2 text-sm text-crimson">{tierNote}</p>
          ) : null}
          {quote ? (
            <p className="mt-3 text-sm text-ink" data-testid="hawan-quote">
              {quote.line}
            </p>
          ) : null}
          <p className="mt-4 text-sm font-semibold text-ink">Food preference</p>
          <div className="mt-2 flex gap-4">
            {(["vegetarian", "jain"] as const).map((f) => (
              <label
                key={f}
                className="flex min-h-11 items-center gap-2 text-sm text-ink"
              >
                <input
                  type="radio"
                  name="food"
                  value={f}
                  checked={value.food === f}
                  disabled={disabled}
                  onChange={() => onChange({ ...value, food: f })}
                  className="h-5 w-5 shrink-0 accent-magenta"
                />
                {f === "vegetarian" ? "Vegetarian" : "Jain"}
              </label>
            ))}
          </div>
        </DayCard>
      </div>
    </fieldset>
  );
}
